import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { Autocomplete } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import { FeatureFlag } from '../types';

export const FeaturesListingMonitoringSubpageCard = (props: {
  selectedFlag: FeatureFlag | null;
  setSelectedFlagId: (id: string) => void;
  featureFlags: FeatureFlag[];
}) => {
  const { selectedFlag, setSelectedFlagId, featureFlags } = props;
  const theme = useTheme();

  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    featureFlags.forEach(f => f.tags?.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, [featureFlags]);

  const filteredFlags = useMemo(() => {
    return featureFlags.filter(flag => {
      const matchesSearch = flag.code.toLowerCase().includes(search.toLowerCase());
      const matchesTags = selectedTags.length === 0 || selectedTags.some(t => (flag.tags ?? []).includes(t));
      return matchesSearch && matchesTags;
    });
  }, [featureFlags, search, selectedTags]);

  useEffect(() => {
    setSearch('');
    setSelectedTags([]);
  }, [featureFlags]);

  return (
    <>
      <Box
        style={{
          width: 400,
          overflow: 'auto',
          scrollbarWidth: 'thin',
          paddingRight: 8,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gridRowGap={8}
          mb={2}
          mt={1}
        >
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            color="secondary"
            fullWidth
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            multiple
            options={allTags}
            value={selectedTags}
            onChange={(_, value) => setSelectedTags(value)}
            disableCloseOnSelect
            size="small"
            fullWidth
            renderOption={(option, { selected }) => (
              <>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  size="small"
                  style={{ marginRight: 4, padding: 2 }}
                />
                {option}
              </>
            )}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => (
                <Chip key={tag} label={tag} size="small" style={{ height: 18, fontSize: 10 }} {...getTagProps({ index })} />
              ))
            }
            renderInput={params => (
              <TextField {...params} label="Tags" variant="outlined" />
            )}
          />
        </Box>
        {filteredFlags.map(flag => (
          <Card
            key={flag.code}
            style={{
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2],
              marginBottom: 16,
              borderBottom:
                selectedFlag?.code === flag.code
                  ? `4px solid ${theme.palette.secondary.main}`
                  : 'none',
            }}
            onClick={() => {
              if (flag.id) {
                setSelectedFlagId(flag.id);
              }
            }}
          >
            <CardContent style={{ cursor: 'pointer' }}>
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {flag.code}
                </Typography>
                <Box display="flex" alignItems="center" ml={1}>
                  <Chip
                    label={
                      flag.state.toUpperCase() === 'ACTIVE'
                        ? 'Enabled'
                        : 'Disabled'
                    }
                    style={{
                      backgroundColor:
                        flag.state.toUpperCase() === 'ACTIVE'
                          ? '#4CAF50'
                          : '#9E9E9E',
                      color: '#fff',
                      fontWeight: 'bold',
                      margin: 0,
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </>
  );
};
